/**
 * Phase 8 T-12 §12 A — Live interview page (recruiter view).
 *
 * Route: `/ai-interviews/:sessionId/live`. Mounts `InterviewLiveLayout`
 * over the app shell. Live-streaming / recording pipeline is not wired
 * in this build (see T-12 DON'Ts) — the layout renders a placeholder
 * canvas and exposes toggle state for the action strip. Pressing
 * `Leave` returns to the session detail page.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@core/auth/useAuth';
import {
  aiInterviewSessionService,
  type AiInterviewSessionHubDetailRow,
} from '@core/supabase/services/aiInterviewSessionService';
import { candidateService } from '@core/supabase/services/candidateService';
import { applicationService } from '@core/supabase/services/applicationService';
import type { Application, Candidate } from '@core/types';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { InterviewLiveLayout, type InterviewLiveAttendee } from './components/InterviewLiveLayout';

function prepUrlForToken(token: string): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/interview/prep/${token}`;
}

const ROUND_LABEL: Record<string, string> = {
  screening: 'Screening',
  technical: 'Technical',
  behavioral: 'Behavioral',
};

export function InterviewLivePage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const [session, setSession] = useState<AiInterviewSessionHubDetailRow | null>(null);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);

  const [elapsed, setElapsed] = useState(0);
  const [recording, setRecording] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoOn, setVideoOn] = useState(true);
  const [presenting, setPresenting] = useState(false);

  useEffect(() => {
    if (!orgId || !sessionId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const s = await aiInterviewSessionService.getByIdForOrg(orgId, sessionId);
        if (cancelled || !s) {
          setSession(null);
          return;
        }
        setSession(s);
        const [c, a] = await Promise.all([
          candidateService.getById(s.candidate_id),
          s.application_id ? applicationService.getById(s.application_id) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setCandidate(c);
        setApplication(a);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, sessionId]);

  useEffect(() => {
    const id = window.setInterval(() => setElapsed((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const companyAttendees: InterviewLiveAttendee[] = useMemo(() => {
    const rows: InterviewLiveAttendee[] = [];
    if (user?.name) {
      rows.push({
        id: user.orgUserId ?? user.id ?? 'host',
        name: user.name,
        email: user.email ?? null,
        role: 'host',
      });
    }
    if (session?.initiator?.display_name && session.initiator.display_name !== user?.name) {
      rows.push({
        id: session.initiator.id,
        name: session.initiator.display_name,
        email: session.initiator.email ?? null,
        role: 'panelist',
      });
    }
    return rows;
  }, [user, session]);

  const goBack = useCallback(() => {
    if (sessionId) {
      navigate(`/ai-interviews/${sessionId}`);
    } else {
      navigate('/ai-interviews');
    }
  }, [navigate, sessionId]);

  if (!orgId) return null;
  if (loading) return <FullPageSpinner />;

  if (!session || !candidate) {
    return (
      <div className="p-6 text-[13px] vscode-text-secondary">
        <p>Could not load this interview session.</p>
        <Link to="/ai-interviews" className="text-[color:var(--vscode-text-link)] hover:underline">
          ← Back to AI interviews
        </Link>
      </div>
    );
  }

  const meetingTitle = `${ROUND_LABEL[session.session_type] ?? session.session_type} — ${candidate.full_name}`;
  const meetingUrl = prepUrlForToken(session.access_token);
  const companyName = user?.orgName ?? application?.client?.name ?? '—';

  return (
    <InterviewLiveLayout
      meetingTitle={meetingTitle}
      meetingUrl={meetingUrl}
      companyName={companyName}
      candidate={candidate}
      companyAttendees={companyAttendees}
      elapsedSeconds={elapsed}
      recording={recording}
      muted={muted}
      videoOn={videoOn}
      presenting={presenting}
      onToggleRecording={() => setRecording((v) => !v)}
      onToggleMute={() => setMuted((v) => !v)}
      onToggleVideo={() => setVideoOn((v) => !v)}
      onTogglePresent={() => setPresenting((v) => !v)}
      onLeave={goBack}
    />
  );
}
